import { useEffect } from 'react';
import {
  trackFetchEagleEyeCookie,
  trackFetchEagleEyePreview,
  trackVideoLoadStart,
  trackVideoLoadedData,
  trackVideoPause,
  trackVideoPlay,
} from './trackings';

/**
 * This fetches a cookie from the EEN session endpoint
 * Without a cookie on the browser, the preview image will fail to load
 */
export const fetchEENCookie = (
  url,
  token,
  cameraId,
  setIsLoading,
  setIsError,
  updateUrl,
) => {
  const requestOptions = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  };

  return fetch(`${url}/api/v3.0/media/session`, requestOptions)
    .then(response => response.json())
    .then(body => fetch(body.url, requestOptions))
    .then(() => {
      trackFetchEagleEyeCookie(true, cameraId);
      updateUrl();
    })
    .catch(() => {
      trackFetchEagleEyeCookie(false, cameraId);
      setIsError(true);
      setIsLoading(false);
    });
};

/**
 * Requests that the element on this document which is currently being presented
 * in fullscreen mode be taken out of fullscreen mode,
 * restoring the previous state of the screen
 */
export const exitFullscreen = () => {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.mozExitFullScreen) {
    document.mozExitFullScreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
};

/**
 * Adds handling and gainsight tracking of on the Preview component for when it loads and errors.
 * This adds an additional check if the preview endpoint fails after fetching the EEN cookie
 */
export const usePreviewEventListeners = (
  previewRef,
  setIsError,
  imageUrl,
  id,
  setIsLoading,
) => {
  useEffect(() => {
    const currentPreview = previewRef.current;

    if (currentPreview?.src) {
      currentPreview.onload = () => {
        trackFetchEagleEyePreview(true, id);
        setIsLoading(false);
      };

      currentPreview.onerror = () => {
        setIsError(true);
        setIsLoading(false);
        trackFetchEagleEyePreview(false, id);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);
};

/**
 * Adds handling and gainsight tracking of on the EagleEyePlayer component for when it loads
 * Currently listens to loadstart, loadeddata, play, pause
 */
export const useVideoEventListeners = (
  videoRef,
  setIsPlayerLoading,
  cameraId,
) => {
  const handleLoadStart = () => {
    setIsPlayerLoading(false);
    trackVideoLoadStart(cameraId);
  };

  useEffect(() => {
    const current = videoRef.current;

    if (current) {
      current.addEventListener('loadstart', handleLoadStart);
      current.addEventListener('play', () => trackVideoPlay(cameraId));
      current.addEventListener('pause', () => trackVideoPause(cameraId));
      current.addEventListener('loadeddata', () =>
        trackVideoLoadedData(cameraId),
      );

      // Forces the browser to exit fullscreen mode when the user clicks the fullscreen button inside the video
      // since it looks like the video is fullscreen but its actually Player that is in fullscreen
      current.addEventListener('fullscreenchange', exitFullscreen);
    }

    return () => {
      if (current) {
        current.removeEventListener('loadstart', handleLoadStart);
        current.removeEventListener('play', () => trackVideoPlay(cameraId));
        current.removeEventListener('pause', () => trackVideoPause(cameraId));
        current.removeEventListener('loadeddata', () =>
          trackVideoLoadedData(cameraId),
        );
        current.removeEventListener('fullscreenchange', exitFullscreen);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
